import { useEffect, useRef, useState } from 'react';
import { Tabs, Toast } from '@ds';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { ProfileActionType, useUserProfileStore } from 'src/stores/UserProfile';
import BasicInformation from './BasicInformation/BasicInformation';
import ContactInformation from './ContactInformation/ContactInformation';
import EmploymentInformation from './EmploymentInformation/EmploymentInformation';
import EducationInformation from './EducationInformation/EducationInformation';
import ProfileChangeAlert from './ProfileChangeAlert/ProfileChangeAlert';
import { useUpdateBeneficiaryDetails } from 'src/shared/mutations/useUpdateBeneficiaryDetails';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useUpdateDependentDetails } from 'src/shared/mutations/useUpdateDependentDetails';
import { useAddNewDependent } from 'src/shared/mutations/useAddNewDependent';
import { useUpdateContactPhoneDetails } from 'src/shared/mutations/ContactPhone/useUpdateContactPhoneDetails';
import { useAddContactPhoneDetails } from 'src/shared/mutations/ContactPhone/useAddContactPhoneDetails';
import { useDeleteContactPhoneDetails } from 'src/shared/mutations/ContactPhone/useDeleteContactPhoneDetails';
import { useAddContactEmail } from 'src/shared/mutations/ContactEmail/useAddContactEmail';
import { useUpdateContactEmail } from 'src/shared/mutations/ContactEmail/useUpdateContactEmail';
import { useDeleteContactEmail } from 'src/shared/mutations/ContactEmail/useDeleteContactEmail';
import { useAddContactAddress } from 'src/shared/mutations/ContactAddress/useAddContactAddress';
import { useUpdateContactAddress } from 'src/shared/mutations/ContactAddress/useUpdateContactAddress';
import { useDeleteContactAddress } from 'src/shared/mutations/ContactAddress/useDeleteContactAddress';
import { EDUCATION_HISTORIES, USER_CONTACTS_KEY } from 'src/utils/constants/queryKeys';
import { useAddEducationHistory } from 'src/shared/mutations/EducationHistory/useAddEducationHistory';
import { useUpdateEducationHistory } from 'src/shared/mutations/EducationHistory/useUpdateEducationHistory';
import { useDeleteEducationHistory } from 'src/shared/mutations/EducationHistory/useDeleteEducationHistory';
import { useGetBeneficiary } from 'src/shared/queries/useGetBeneficiary';
import { Relationship } from 'src/shared/types/Common';
import { CONTACT_INFORMATION_TAB_INDEX, INTERCOM_VERTICAL_PADDING } from 'src/utils/constants/common';
import { IntercomService } from 'src/utils/services/Intercom';
import { useThemeStore } from 'src/stores/ThemeStore';
import styles from './ProfileTabs.module.scss';

const ProfileTabs: React.FC = () => {
  const { isDeloitteTheme } = useThemeStore();
  const queryClient = useQueryClient();
  const {
    editMode,
    updatedFields,
    profileInfo,
    setProfileInfo,
    setProfileActionHandler,
    toggleEditMode,
    setUpdatedFields,
  } = useUserProfileStore();
  const { data: userProfile } = useGetUser();
  const beneId = userProfile?.beneId ?? 0;
  const { data: beneInfo } = useGetBeneficiary(beneId);
  const { isDesktop, isMobile } = useScreenSize();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [actionTriggerCount, setActionTriggerCount] = useState(0);
  const toastRef = useRef<IToastr>(null);
  const { mutateAsync: updateBeneficiaryDetails } = useUpdateBeneficiaryDetails();
  const { mutate: updateDependentDetails } = useUpdateDependentDetails();
  const { mutateAsync: addNewDependent } = useAddNewDependent();
  const { mutateAsync: addContactPhone } = useAddContactPhoneDetails();
  const { mutateAsync: updateContactPhone } = useUpdateContactPhoneDetails();
  const { mutateAsync: deleteContactPhone } = useDeleteContactPhoneDetails();
  const { mutateAsync: addContactEmail } = useAddContactEmail();
  const { mutateAsync: updateContactEmail } = useUpdateContactEmail();
  const { mutateAsync: deleteContactEmail } = useDeleteContactEmail();
  const { mutateAsync: addContactAddress } = useAddContactAddress();
  const { mutateAsync: updateContactAddress } = useUpdateContactAddress();
  const { mutateAsync: deleteContactAddress } = useDeleteContactAddress();
  const { mutateAsync: addEducationHistory } = useAddEducationHistory();
  const { mutateAsync: updateEducationHistory } = useUpdateEducationHistory();
  const { mutateAsync: deleteEducationHistory } = useDeleteEducationHistory();
  const isPrincipal = profileInfo?.isPrincipal;
  const isAddNewDependent = profileInfo?.id === -1;
  const userId = userProfile?.userId;

  useEffect(() => {
    const alertBar = document.getElementById('profile-change-alert-bar');
    if (isDesktop) {
      const tabViewNavContainer = document.querySelector('.p-tabview-nav-container') as HTMLDivElement;
      if (tabViewNavContainer) {
        tabViewNavContainer.style.position = editMode ? 'sticky' : '';
        tabViewNavContainer.style.top = editMode ? '266px' : '';
        tabViewNavContainer.style.zIndex = editMode ? '1002' : '';
      }
    }
    if (isMobile) {
      const tabViewPanels = document
        .querySelector('#user-profile-content')
        ?.querySelector('[class*="profile-tabs"]') as HTMLDivElement;
      if (tabViewPanels && alertBar) {
        tabViewPanels.style.marginTop = `${editMode ? alertBar.clientHeight + 4 : 0}px`;
      }
    } else {
      const tabViewPanels = document.querySelector('#user-profile-content .p-tabview-panels') as HTMLDivElement;
      if (tabViewPanels && alertBar) {
        tabViewPanels.style.marginTop = `${editMode ? alertBar.clientHeight + 4 : 0}px`;
      }
    }
  }, [editMode, isDesktop]);

  const resetIntercomPadding = () => {
    IntercomService.update({
      vertical_padding: 0,
    });
  };

  const setIntercomPadding = () => {
    IntercomService.update({
      vertical_padding: INTERCOM_VERTICAL_PADDING,
    });
  };

  useEffect(() => {
    if (activeTabIndex === CONTACT_INFORMATION_TAB_INDEX) {
      setIntercomPadding();
    } else {
      resetIntercomPadding();
    }
  }, [activeTabIndex]);

  useEffect(() => {
    return () => {
      resetIntercomPadding();
    };
  }, []);

  useEffect(() => {
    setActiveTabIndex(0);
  }, [profileInfo?.id]);

  useEffect(() => {
    setProfileActionHandler((actionType) => {
      switch (actionType) {
        case ProfileActionType.UNDO:
          setUpdatedFields(null);
          setActionTriggerCount((prev) => prev + 1);
          if (profileInfo?.id === -1) {
            setProfileInfo({
              id: beneId,
              fullName: beneInfo?.fullName,
              relationship: Relationship.Principal,
              isPrincipal: true,
            });
          }
          break;
        case ProfileActionType.SAVE:
          if (!updatedFields) return;

          onUpdateStart();
          if (activeTabIndex === 0) {
            isAddNewDependent ? onAddNewDependent() : onUpdateBasicInformationDetails();
          } else if (activeTabIndex === 1) {
            const promises: Promise<unknown>[] = [];

            if (updatedFields.contactPhones) {
              promises.push(onUpdateContactPhones(updatedFields.contactPhones as Record<string, object[]>));
            }
            if (updatedFields.contactEmails) {
              promises.push(onUpdateContactEmails(updatedFields.contactEmails as Record<string, object[]>));
            }
            if (updatedFields.contactAddresses) {
              promises.push(onUpdateContactAddresses(updatedFields.contactAddresses as Record<string, object[]>));
            }

            Promise.all(promises)
              .then(() => {
                queryClient.refetchQueries({
                  queryKey: [USER_CONTACTS_KEY, isPrincipal ? userId : profileInfo?.id, isPrincipal],
                });
                onSuccess();
              })
              .catch(onError);
          } else if (activeTabIndex === 2) {
            const promises: Promise<unknown>[] = [];
            if (updatedFields.employmentInformation) {
              const details = updatedFields.employmentInformation as Record<string, unknown>;
              details.beneId = profileInfo?.id;
              promises.push(updateBeneficiaryDetails(details));
            }
            if (updatedFields.contactAddresses) {
              promises.push(onUpdateContactAddresses(updatedFields.contactAddresses as Record<string, object[]>));
            }
            Promise.all(promises)
              .then(() => {
                queryClient.refetchQueries({
                  queryKey: [USER_CONTACTS_KEY, isPrincipal ? userId : profileInfo?.id, isPrincipal],
                });
                onSuccess();
              })
              .catch(onError);
          } else if (activeTabIndex === 3) {
            if (updatedFields.educationHistory) {
              const promises: Promise<unknown>[] = [];
              const list = updatedFields.educationHistory as Record<string, object[]>;
              promises.push(onUpdateEducationHistory(list));
              Promise.all(promises)
                .then(() => {
                  queryClient
                    .refetchQueries({ queryKey: [EDUCATION_HISTORIES, profileInfo?.id] })
                    .then(onSuccess)
                    .catch(onError);
                })
                .catch(onError);
            }
          }
          break;
      }
    });
  }, [updatedFields]);

  const onUpdateBasicInformationDetails = () => {
    if (!profileInfo || !updatedFields) return;

    const payload = {
      ...(updatedFields.basicInformation as object),
      [isPrincipal ? 'beneId' : 'dependentId']: profileInfo.id,
    };
    const mutationFn = isPrincipal ? updateBeneficiaryDetails : updateDependentDetails;

    mutationFn(payload, { onSuccess, onError });
  };

  const onAddNewDependent = () => {
    const payload = { ...(updatedFields?.basicInformation as object), beneId };
    if ('isNew' in payload) delete payload.isNew;
    addNewDependent(payload, {
      onSuccess: (dependent) => {
        updateDependentDetails(
          { ...payload, dependentId: dependent.dependentId },
          {
            onSuccess: () => {
              onSuccess();
              setProfileInfo({
                id: dependent.dependentId,
                fullName: dependent?.fullName,
                firstName: dependent.firstName,
                lastName: dependent.lastName,
                relationship: dependent.relationship,
                isPrincipal: false,
              });
            },
            onError,
          }
        );
      },
      onError,
    });
  };

  const processListUpdates = async (
    data: Record<string, object[]>,
    addFn: (item: Record<string, unknown>) => Promise<unknown>,
    editFn: (item: Record<string, unknown>) => Promise<unknown>,
    deleteFn: (id: number) => Promise<unknown>
  ) => {
    const promises: Promise<unknown>[] = [];

    if (data.add?.length > 0) {
      data.add.forEach((item) => promises.push(addFn(item as Record<string, unknown>)));
    }

    if (data.edit?.length > 0) {
      data.edit.forEach((item) => promises.push(editFn(item as Record<string, unknown>)));
    }

    if (data.delete?.length > 0) {
      data.delete.forEach((id) => promises.push(deleteFn(id as unknown as number)));
    }

    await Promise.all(promises);
  };

  const onUpdateContactPhones = async (contactPhones: Record<string, object[]>) => {
    await processListUpdates(contactPhones, addContactPhone, updateContactPhone, deleteContactPhone);
  };

  const onUpdateContactEmails = async (contactEmails: Record<string, object[]>) => {
    await processListUpdates(contactEmails, addContactEmail, updateContactEmail, deleteContactEmail);
  };

  const onUpdateContactAddresses = async (contactAddresses: Record<string, object[]>) => {
    await processListUpdates(contactAddresses, addContactAddress, updateContactAddress, deleteContactAddress);
  };

  const onUpdateEducationHistory = async (educationHistory: Record<string, object[]>) => {
    await processListUpdates(educationHistory, addEducationHistory, updateEducationHistory, deleteEducationHistory);
  };

  const onUpdateStart = () => {
    toastRef.current?.show({ severity: 'info' });
    const bodyElement = document.querySelector('body') as HTMLBodyElement;
    bodyElement.style.cursor = 'not-allowed';
    bodyElement.style.pointerEvents = 'none';
  };

  const onUpdateComplete = () => {
    toastRef.current?.clear();
    const bodyElement = document.querySelector('body') as HTMLBodyElement;
    bodyElement.style.cursor = '';
    bodyElement.style.pointerEvents = '';
  };

  const onSuccess = () => {
    onUpdateComplete();
    toastRef.current?.show({ message: 'Saved', severity: 'success' });
    toggleEditMode();
    setUpdatedFields(null);
    setActionTriggerCount((prev) => prev + 1);
  };

  const onError = () => {
    onUpdateComplete();
    toastRef.current?.show({
      message: 'There was an error while saving data. Try again later.',
      severity: 'error',
    });
  };

  const tabs = [
    { header: 'Basic Information', content: <BasicInformation /> },
    { header: 'Contact Information', content: <ContactInformation /> },
    { header: 'Employment Information', disabled: !isPrincipal, content: <EmploymentInformation /> },
    { header: 'Education Information', disabled: !isPrincipal, content: <EducationInformation /> },
  ];

  return (
    <div className={styles['profile-tabs-container']}>
      {(isDeloitteTheme || activeTabIndex !== 2) && <ProfileChangeAlert />}
      <div id="user-profile-content" key={`profile-tabs-${actionTriggerCount}`}>
        <Tabs
          activeTabIndex={activeTabIndex}
          className={classNames(styles['profile-tabs'], { 'mt-[-58px]': isDesktop && editMode })}
          tabs={tabs}
          onTabChange={setActiveTabIndex}
        ></Tabs>
      </div>
      <Toast ref={toastRef} />
    </div>
  );
};

export default ProfileTabs;
